'use client';

import { useCallback, useState } from 'react';
import { isNil } from 'es-toolkit';
import { downloadTextContent } from 'src/utils/download-file';

import type { ExportableColumn, ExportFormat, ExportState } from '@savvy/data-table';

import type { AccountsDataTableRowUnmasked } from './accounts-data-table-columns';

const EXPORT_COLUMNS: ExportableColumn[] = [
  { id: 'accountName', header: 'Account Name', isVisible: true },
  { id: 'accountNumber', header: 'Account Number', isVisible: true },
  { id: 'household', header: 'Household', isVisible: true },
  { id: 'primaryAdvisor', header: 'Primary Advisor', isVisible: true },
  { id: 'balance', header: 'Balance', isVisible: true },
  { id: 'strategyName', header: 'Strategy', isVisible: true },
  { id: 'feeSchedule', header: 'Fee Schedule', isVisible: true },
  { id: 'billFrequency', header: 'Frequency', isVisible: true },
  { id: 'billStyle', header: 'Bill Style', isVisible: true },
  { id: 'payMethod', header: 'Pay Method', isVisible: true },
  { id: 'startDate', header: 'Start Date', isVisible: true },
  { id: 'recurringAdjustmentStartDate', header: 'Recurring Adjustment Start Date', isVisible: true },
  { id: 'recurringAdjustmentEndDate', header: 'Recurring Adjustment End Date', isVisible: true },
  { id: 'recurringAdjustmentSchedule', header: 'Recurring Adjustment Schedule', isVisible: true },
  { id: 'feeType', header: 'Fee Type', isVisible: true },
  { id: 'amountType', header: 'Amount Type', isVisible: true },
  { id: 'flatFeeAmount', header: 'Flat Fee Amount', isVisible: true },
  { id: 'minimumFee', header: 'Minimum Fee', isVisible: true },
];

const DEFAULT_EXPORT_COLUMN_IDS = EXPORT_COLUMNS.filter((c) => c.isVisible).map((c) => c.id);
const USER_TEXT_COLUMN_IDS: ReadonlySet<string> = new Set([
  'accountName',
  'accountNumber',
  'household',
  'primaryAdvisor',
  'strategyName',
  'feeSchedule',
  'billFrequency',
  'billStyle',
  'payMethod',
  'recurringAdjustmentSchedule',
]);
const EXPORT_FORMATS: ExportFormat[] = ['csv'];

function getColumnValue(row: AccountsDataTableRowUnmasked, columnId: string): string | number | null {
  switch (columnId) {
    case 'accountName':
      return row.displayName;
    case 'accountNumber':
      return row.accountNumber ?? null;
    case 'household':
      return row.portfolio?.household.name ?? null;
    case 'primaryAdvisor': {
      const advisorUser = row.portfolio?.household.advisor?.user;
      if (isNil(advisorUser)) return 'Unassigned';
      return `${advisorUser.firstName} ${advisorUser.lastName}`.trim();
    }
    case 'balance':
      return Number(row.displayBalance.amount);
    case 'strategyName':
      return row.strategyName ?? null;
    case 'feeSchedule':
      return row.orionFeeSchedule ?? null;
    case 'billFrequency':
      return row.orionBillFrequency ?? null;
    case 'billStyle':
      return row.orionBillStyle ?? null;
    case 'payMethod':
      return row.orionBillPayMethod ?? null;
    case 'startDate':
      return row.orionAccountStartDate ?? null;
    case 'recurringAdjustmentStartDate':
      return row.recurringAdjustmentBillStartDate ?? null;
    case 'recurringAdjustmentEndDate':
      return row.recurringAdjustmentBillEndDate ?? null;
    case 'recurringAdjustmentSchedule':
      return row.recurringAdjustmentBillScheduleName ?? null;
    case 'feeType':
      return row.activeFeeSchedule?.feeType ?? null;
    case 'amountType':
      return row.activeFeeSchedule?.amountType ?? null;
    case 'flatFeeAmount':
      return row.activeFeeSchedule?.flatFeeAmount ?? null;
    case 'minimumFee':
      return row.activeFeeSchedule?.minimumFee ?? null;
    default:
      return null;
  }
}

export function buildCSV(rows: readonly AccountsDataTableRowUnmasked[], cols: readonly ExportableColumn[]): string {
  const header = cols.map((c) => `"${c.header}"`).join(',');
  const dataRows = rows.map((row) =>
    cols
      .map((c) => {
        const val = getColumnValue(row, c.id);
        if (typeof val === 'number') return String(val);
        if (val === null) return '""';
        const escaped = val.replace(/"/g, '""');
        if (USER_TEXT_COLUMN_IDS.has(c.id) && /^[=+\-@]/.test(val)) {
          return `"'${escaped}"`;
        }
        return `"${escaped}"`;
      })
      .join(','),
  );
  return [header, ...dataRows].join('\n');
}

export function useAccountsExport(rows: readonly AccountsDataTableRowUnmasked[]) {
  const [open, setOpen] = useState(false);
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([...DEFAULT_EXPORT_COLUMN_IDS]);
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('csv');
  const [state, setState] = useState<ExportState>({ status: 'idle', error: null });

  const selectedColumns = EXPORT_COLUMNS.filter((c) => selectedColumnIds.includes(c.id));
  const availableColumns = EXPORT_COLUMNS.filter((c) => !selectedColumnIds.includes(c.id));
  const canExport = selectedColumnIds.length > 0;

  const addColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) => (prev.includes(columnId) ? prev : [...prev, columnId]));
  }, []);

  const removeColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) => prev.filter((id) => id !== columnId));
  }, []);

  const toggleColumn = useCallback((columnId: string) => {
    setSelectedColumnIds((prev) =>
      prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
    );
  }, []);

  const reset = useCallback(() => {
    setSelectedColumnIds([...DEFAULT_EXPORT_COLUMN_IDS]);
    setSelectedFormat('csv');
  }, []);

  const onOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setState({ status: 'idle', error: null });
  }, []);

  const execute = useCallback((): Promise<void> => {
    setState({ status: 'exporting', error: null });
    try {
      const cols = EXPORT_COLUMNS.filter((c) => selectedColumnIds.includes(c.id));
      const date = new Date().toISOString().slice(0, 10);
      downloadTextContent({
        content: buildCSV(rows, cols),
        filename: `accounts-${date}.csv`,
        mimeType: 'text/csv;charset=utf-8;',
      });
      setState({ status: 'success', error: null });
    } catch (err) {
      setState({ status: 'error', error: err instanceof Error ? err.message : 'Export failed.' });
    }
    return Promise.resolve();
  }, [rows, selectedColumnIds]);

  return {
    open,
    onOpenChange,
    setModalOpen: setOpen,
    columns: EXPORT_COLUMNS,
    availableColumns,
    selectedColumns,
    selectedColumnIds,
    selectedFormat,
    state,
    canExport,
    formats: EXPORT_FORMATS,
    setSelectedFormat,
    toggleColumn,
    addColumn,
    removeColumn,
    reset,
    execute,
  };
}
